
import React from "react";
import PageLayout from "@/components/PageLayout";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

const Pricing = () => {
  const pricingPlans = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      description: "Basic access for job seekers",
      features: [
        "Search company reviews",
        "Read basic reviews",
        "Submit up to 3 reviews",
        "Basic company information",
      ],
      cta: "Get Started",
      popular: false,
    },
    {
      name: "Pro",
      price: "$9.99",
      period: "per month",
      description: "Enhanced access for active job seekers",
      features: [
        "Everything in Free",
        "Advanced filtering options",
        "Unlimited review submissions",
        "Detailed company insights",
        "Salary information",
        "Early access to new features",
      ],
      cta: "Try Pro",
      popular: true,
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "per organization",
      description: "For HR departments and recruiters",
      features: [
        "Everything in Pro",
        "Company response options",
        "Employer branding",
        "Analytics dashboard",
        "API access",
        "Dedicated support",
      ],
      cta: "Contact Sales",
      popular: false,
    },
  ];

  return (
    <PageLayout 
      title="Pricing" 
      description="Choose the plan that's right for you"
    >
      <div className="space-y-16">
        {/* Pricing Cards */}
        <section className="grid md:grid-cols-3 gap-8">
          {pricingPlans.map((plan, index) => (
            <Card 
              key={index} 
              className={`flex flex-col ${plan.popular ? 'border-redflags-red shadow-md relative' : ''}`}
            >
              {plan.popular && (
                <span className="absolute -top-3 right-8 bg-redflags-red text-white text-xs font-bold py-1 px-3 rounded-full">
                  Popular
                </span>
              )}
              <CardHeader>
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <div className="mt-2">
                  <span className="text-3xl font-bold">{plan.price}</span>
                  <span className="text-redflags-gray ml-1">/{plan.period}</span>
                </div>
                <CardDescription className="mt-2">{plan.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <ul className="space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-redflags-red flex-shrink-0" />
                      <span className="text-sm text-redflags-gray">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button 
                  className={`w-full ${plan.popular ? 'bg-redflags-red hover:bg-redflags-red/90' : ''}`}
                  variant={plan.popular ? 'default' : 'outline'}
                >
                  {plan.cta}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </section>

        {/* FAQ Section */}
        <section>
          <h2 className="text-2xl font-semibold text-redflags-dark text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
              <h3 className="font-semibold text-lg text-redflags-dark mb-2">Can I use RedFlags for free?</h3>
              <p className="text-redflags-gray">Yes, our Free plan provides basic access for job seekers to search and read company reviews, as well as submit a limited number of reviews.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
              <h3 className="font-semibold text-lg text-redflags-dark mb-2">How does billing work for Pro plans?</h3>
              <p className="text-redflags-gray">Pro plans are billed monthly with the option to cancel anytime. We also offer annual plans at a discounted rate.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
              <h3 className="font-semibold text-lg text-redflags-dark mb-2">What's included in the Enterprise plan?</h3>
              <p className="text-redflags-gray">Enterprise plans are tailored to organizations and include company response options, employer branding tools, analytics dashboards, API access, and dedicated support.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
              <h3 className="font-semibold text-lg text-redflags-dark mb-2">Can I upgrade or downgrade my plan?</h3>
              <p className="text-redflags-gray">Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.</p>
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};

export default Pricing;
